import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import apiClient from 'src/helpers/apiClient';
import { getFormattedErr } from 'src/helpers';
import type { CandidateShowcaseSub } from 'src/openapi';
import type { FreeUnlockPromo } from '../types/employerCredit';

export type ShowcaseCandidate = CandidateShowcaseSub;

export const fetchCreditBalance = createAsyncThunk(
  'employerCredit/fetchBalance',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getEmployerCreditApi()
      ).employerCreditApiGetEmployerCredits({ orgId });
      // `promo` (the free-unlock offer window) is widened in by hand until the
      // generated client is refreshed with `npm run update-spec`.
      return response.data as typeof response.data & {
        promo?: FreeUnlockPromo;
      };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

/** Claim the running free-unlock offer for this org — the invite link's popup. */
export const activateFreeUnlockOffer = createAsyncThunk(
  'employerCredit/activateFreeUnlockOffer',
  async (
    { orgId, offerId }: { orgId: string; offerId?: string },
    { rejectWithValue },
  ) => {
    try {
      const data = await apiClient.activateFreeUnlockOffer(orgId, offerId);
      return data as { balance?: number; promo?: FreeUnlockPromo };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * A member opened an offer's invite link: record their org as invited, so the
 * popup opens for the whole team from now on. Silent on failure — the offer may
 * simply have ended since the link was shared.
 */
export const claimFreeUnlockOfferLink = createAsyncThunk(
  'employerCredit/claimFreeUnlockOfferLink',
  async (
    { orgId, offerId }: { orgId: string; offerId: string },
    { rejectWithValue },
  ) => {
    try {
      const data = await apiClient.claimFreeUnlockOfferLink(orgId, offerId);
      return data as { balance?: number; promo?: FreeUnlockPromo };
    } catch (err: any) {
      // The status tells a real "no" (4xx) from a failure worth retrying.
      return rejectWithValue({
        status: err?.response?.status as number | undefined,
        ...err?.response?.data,
      });
    }
  },
);

export const fetchCreditPacks = createAsyncThunk(
  'employerCredit/fetchPacks',
  async (_arg: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getEmployerCreditApi()
      ).employerCreditApiGetEmployerCreditPacks();
      return response.data.data ?? [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchCreditHistory = createAsyncThunk(
  'employerCredit/fetchHistory',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getEmployerCreditApi()
      ).employerCreditApiGetEmployerCreditHistory({
        orgId,
        page: 1,
        pageSize: 50,
      });
      return response.data.data ?? [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const PIPELINE_PAGE_SIZE = 15;

export const fetchUnlockedCandidates = createAsyncThunk(
  'employerCredit/fetchUnlockedCandidates',
  async (
    {
      orgId,
      page = 1,
      search,
      jobId,
    }: { orgId: string; page?: number; search?: string; jobId?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getEmployerCreditApi()
      ).employerCreditApiGetUnlockedCandidates({
        orgId,
        jobId,
        page,
        pageSize: PIPELINE_PAGE_SIZE,
        search,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// All jobs the org has posted — used for the talent-pipeline job filter dropdown.
export const fetchOrgJobOptions = createAsyncThunk(
  'employerCredit/fetchOrgJobOptions',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobPostedList({
        orgId,
        pageNo: 1,
        pageSize: 200,
      });
      const jobs = (response.data?.jobs ?? []) as Array<{
        id?: string;
        title?: string;
      }>;
      return jobs.map((j) => ({
        id: j.id ?? '',
        title: j.title ?? 'Untitled job',
      }));
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const unlockApplicant = createAsyncThunk(
  'employerCredit/unlockApplicant',
  async (
    {
      orgId,
      jobId,
      applicantId,
    }: { orgId: string; jobId: string; applicantId: string },
    { rejectWithValue },
  ) => {
    try {
      // Generated client orders these params reverse of the path.
      const response = await (
        await apiClient.getEmployerCreditApi()
      ).employerCreditApiUnlockApplicant({ applicantId, jobId, orgId });
      return response.data;
    } catch (err: any) {
      const errorKey = err?.response?.data?.error;
      // Insufficient credits is handled in-slice by opening the buy dialog,
      // so we suppress the generic snackbar for it.
      if (errorKey !== 'insufficient_employer_credits') {
        enqueueSnackbar(getFormattedErr(errorKey), { variant: 'error' });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

export interface ShowcaseResult {
  candidates: ShowcaseCandidate[];
}

export const fetchCandidateShowcase = createAsyncThunk(
  'employerCredit/fetchCandidateShowcase',
  async (arg: { role?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiCandidateShowcase({ role: arg?.role });
      return { candidates: response.data?.candidates ?? [] } as ShowcaseResult;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);
